// B-165. How the Cierres assistant's reply is drawn: its markdown with GFM (tables, strikethrough,
// task lists) and a style per element in the Brinks theme, since Tailwind's preflight takes every
// list bullet, heading size and table border away. The spacing between blocks is the container's
// alone: `whitespace-pre-line` sits on a paragraph, never around the whole reply, where it drew
// the line breaks ReactMarkdown leaves between blocks as blank lines.
import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Check, Copy, RefreshCw } from "lucide-react";

import { linkifyClosingCitations } from "../../../utils/closingCitations";
import { alinearCifras, prepararRespuesta } from "../../../utils/respuestaAsistente";

const alineacion = (style) => (style?.textAlign ? { textAlign: style.textAlign } : undefined);

const COMPONENTES = {
  p: ({ node, ...props }) => <p className="whitespace-pre-line break-words" {...props} />,
  h1: ({ node, children, ...props }) => (
    <h3 className="pt-1 text-base font-semibold text-brinks-foreground" {...props}>
      {children}
    </h3>
  ),
  h2: ({ node, children, ...props }) => (
    <h4 className="pt-1 text-[15px] font-semibold text-brinks-foreground" {...props}>
      {children}
    </h4>
  ),
  h3: ({ node, children, ...props }) => (
    <h5 className="text-sm font-semibold text-brinks-foreground" {...props}>
      {children}
    </h5>
  ),
  h4: ({ node, children, ...props }) => (
    <h6 className="text-sm font-semibold text-brinks-foreground" {...props}>
      {children}
    </h6>
  ),
  ul: ({ node, ...props }) => (
    <ul className="list-disc space-y-1.5 pl-5 marker:text-brinks-muted-foreground [&_ol]:mt-1.5 [&_ul]:mt-1.5" {...props} />
  ),
  ol: ({ node, ...props }) => (
    <ol className="list-decimal space-y-1.5 pl-5 marker:text-brinks-muted-foreground [&_ol]:mt-1.5 [&_ul]:mt-1.5" {...props} />
  ),
  li: ({ node, ...props }) => <li className="pl-1 [&>p+p]:mt-1.5" {...props} />,
  strong: ({ node, ...props }) => <strong className="font-semibold text-brinks-foreground" {...props} />,
  blockquote: ({ node, ...props }) => (
    <blockquote className="border-l-2 border-brinks-border pl-3 text-brinks-muted-foreground" {...props} />
  ),
  hr: () => <hr className="border-brinks-border" />,
  pre: ({ node, ...props }) => (
    <pre
      className="overflow-x-auto rounded-[10px] bg-brinks-surface p-3 text-xs leading-relaxed [&>code]:bg-transparent [&>code]:p-0"
      {...props}
    />
  ),
  code: ({ node, ...props }) => (
    <code className="rounded bg-brinks-surface px-1 py-0.5 font-mono text-[12.5px]" {...props} />
  ),
  // No frame of its own inside the reply's card: a grey band of headings with rounded ends and a
  // hairline between rows. `border-separate` is what lets the band's ends round.
  table: ({ node, ...props }) => (
    <div className="overflow-x-auto">
      <table className="w-full border-separate border-spacing-0 text-[13px] leading-snug tabular-nums" {...props} />
    </div>
  ),
  thead: ({ node, ...props }) => <thead className="bg-brinks-surface" {...props} />,
  tr: ({ node, ...props }) => <tr className="transition-colors [tbody>&]:hover:bg-brinks-surface-hover" {...props} />,
  th: ({ node, style, ...props }) => (
    <th
      className="whitespace-nowrap px-3 py-2 text-left text-xs font-semibold text-brinks-foreground first:rounded-l-md last:rounded-r-md"
      style={alineacion(style)}
      {...props}
    />
  ),
  // On a phone the first column keeps a readable width (the table scrolls sideways instead) and a
  // figure is never split over two lines.
  td: ({ node, style, ...props }) => (
    <td
      className={`border-t border-brinks-border px-3 py-1.5 align-top text-brinks-foreground first:min-w-[9rem] [tr:first-child>&]:border-t-0${
        style?.textAlign === "right" ? " whitespace-nowrap" : ""
      }`}
      style={alineacion(style)}
      {...props}
    />
  ),
};

// What a pasted reply keeps in an email, where the app's classes do not exist: the table's lines,
// padding and alignment written on each element.
function htmlParaCopiar(nodo) {
  const copia = nodo.cloneNode(true);
  copia.querySelectorAll("table").forEach((t) => {
    t.setAttribute("style", "border-collapse:collapse;font-size:13px");
  });
  copia.querySelectorAll("th, td").forEach((c) => {
    const alineado = c.style.textAlign || "left";
    const fondo = c.tagName === "TH" ? "background:#f4f4f5;font-weight:600;" : "";
    c.setAttribute("style", `${fondo}border:1px solid #e4e4e7;padding:4px 10px;text-align:${alineado}`);
  });
  return copia.innerHTML;
}

const AVISOS_COPIA = { listo: "Copiado", error: "No se pudo copiar" };

// Copying a finished reply: a round icon button under its card, the way chat assistants offer it —
// not a word in the middle of the answer. It copies what is on screen, formatted (a table pasted into Outlook or Gmail
// stays a table), with plain text beside it for wherever HTML is not taken; the icon turns into a
// check with "Copiado" beside it for two seconds. `obtenerNodo` hands over the reply's rendered text.
export function CopiarRespuesta({ obtenerNodo }) {
  const [estado, setEstado] = useState(null);
  const temporizador = useRef(null);

  useEffect(() => () => clearTimeout(temporizador.current), []);

  const avisar = (nuevo) => {
    setEstado(nuevo);
    clearTimeout(temporizador.current);
    temporizador.current = setTimeout(() => setEstado(null), 2000);
  };

  const copiar = async () => {
    const nodo = obtenerNodo();
    if (!nodo || !navigator.clipboard) return avisar("error");
    const texto = nodo.innerText;
    try {
      if (typeof window.ClipboardItem === "function" && typeof navigator.clipboard.write === "function") {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "text/html": new Blob([htmlParaCopiar(nodo)], { type: "text/html" }),
            "text/plain": new Blob([texto], { type: "text/plain" }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(texto);
      }
      avisar("listo");
    } catch (e) {
      avisar("error");
    }
    return undefined;
  };

  const Icono = estado === "listo" ? Check : Copy;
  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={copiar}
        aria-label="Copiar respuesta"
        title="Copiar respuesta"
        className="grid h-8 w-8 place-items-center rounded-full border border-brinks-border bg-card text-brinks-muted-foreground shadow-brinks-card transition hover:border-brinks-border-strong hover:text-brinks-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brinks-ring"
      >
        <Icono className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      <span aria-live="polite" className="text-xs text-brinks-muted-foreground">
        {estado ? AVISOS_COPIA[estado] : ""}
      </span>
    </div>
  );
}

// B-167: asking for a new answer to the same question — the round button beside "Copiar", in the
// same style. The chat decides where it shows (only under the last real answer) and what it does.
export function RegenerarRespuesta({ onRegenerar }) {
  return (
    <button
      type="button"
      onClick={onRegenerar}
      aria-label="Regenerar respuesta"
      title="Regenerar respuesta"
      className="grid h-8 w-8 place-items-center rounded-full border border-brinks-border bg-card text-brinks-muted-foreground shadow-brinks-card transition hover:border-brinks-border-strong hover:text-brinks-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brinks-ring"
    >
      <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
  );
}

// `contenidoRef` reaches the rendered text, for whoever copies it.
export function RespuestaAsistente({ texto, renderLink, contenidoRef }) {
  const componentes = renderLink
    ? {
        ...COMPONENTES,
        a: (props) =>
          renderLink({ ...props, className: "font-medium underline underline-offset-2 hover:opacity-80" }),
      }
    : COMPONENTES;
  return (
    <div ref={contenidoRef} className="space-y-3 text-sm leading-relaxed text-brinks-foreground">
      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[alinearCifras]} components={componentes}>
        {linkifyClosingCitations(prepararRespuesta(texto))}
      </ReactMarkdown>
    </div>
  );
}
